import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { CoalescedRequest, DelayedRequest, useTimeout } from '@/js/util';
import KeyedVisibilityObserver from './lazy_loading/keyed_visibility_observer';

const BASE_DELAY = 500;
const LOADING_CLASS = 'is-loading';
const LOADED_CLASS = 'is-loaded';
const FAILED_CLASS = 'is-failed';

const randomNumber = (max) => Math.floor(Math.random() * max);

const keyedVisibilityObserver = new KeyedVisibilityObserver();

const resolved = new Map();

const ONE_MINUTE = 1000 * 60;

export default class extends Controller {
  static targets = ['image', 'placeholder'];

  static values = {
    pollPath: String,
    resourceId: String,
    retryPath: String,
    variant: String,
    failureImagePath: String,
    // An image that is ready already: no polling, it just loads behind the placeholder
    imageUrl: String,
  };

  initialize() {
    this.loadedImageSource = null;
    this.placeholderTargetProperties = null;
  }

  connect() {
    useTimeout(this);
    this.deadlineTimeoutId = null;
    // Reset per connection, not per instance: a re-mount (see the resolved "hack" below) keeps
    // the same controller and has to announce itself again for whoever is listening this time.
    this.shown = false;
    this.imageFailed = false;

    // A board template's screenshot is taken while it saves, so by the time the image is rendered
    // there is nothing to wait for: load it and swap it in like a polled one.
    if (this.imageUrlValue) {
      this.#fetchImage(this.imageUrlValue);
      return;
    }

    // This resolved "hack" is needed for a case when image is rendered in a jQuery modal,
    // or in template element or a fetched modal.
    // That library re-mounts existing element, triggering another connect() execution,
    // which then might lead to excessive request, that we should avoid.
    // While when mounting a loader from a template or a fetched modal, the image might have been
    // already resolved by other controller.
    //
    // Currently, this is a case for:
    //   - Designs::EnhancedImages::GeneratorModal
    //   - messages/_board_details
    if (resolved.has(this.pollPathValue)) {
      const data = resolved.get(this.pollPathValue);
      this.loadedImageSource = this.#variantUrl(data);
      // Announced again for listeners that connected after the first success, like a modal fetched since then.
      this.dispatch(`${this.resourceIdValue}:succeeded`, { detail: { data } });
      this.showImage();
      return;
    }

    keyedVisibilityObserver.observe(this.resourceIdValue, this.element, () => {
      this.#schedulePolling(0);
      keyedVisibilityObserver.unobserve(this.resourceIdValue, this.element);
    });
  }

  disconnect() {
    this.#clearDeadline();
    this.pollingRequest?.cancel();
    keyedVisibilityObserver.unobserve(this.resourceIdValue, this.element);
  }

  retry() {
    this.#clearDeadline();

    Object.assign(this.placeholderTarget, this.placeholderTargetProperties);
    this.placeholderTargetProperties = null;

    this.element.classList.remove(FAILED_CLASS);
    this.dispatch(`${this.resourceIdValue}:retried`);
    this.#requestRetry().finally(() => this.#schedulePolling(0));
  }

  showImage(event) {
    if (!this.loadedImageSource) {
      return;
    }

    this.imageTarget.src = this.loadedImageSource;

    this.element.classList.remove(LOADING_CLASS);
    if (event) {
      this.element.classList.add(LOADED_CLASS);
    }

    // Every path that puts the generated image in front of the viewer ends up here: the end of
    // a skeleton animation cycle, the hidden element shortcut in #fetchImage, and the already
    // resolved shortcut in connect(). animationiteration keeps calling this, so announce once.
    // A source that failed to download is still swapped in above, falling back to the browser's
    // broken image, but there is nothing to look at so it is not announced.
    if (!this.shown && !this.imageFailed) {
      this.shown = true;
      this.dispatch('shown');
    }
  }

  removePlaceholder() {
    this.placeholderTarget.remove();
  }

  #schedulePolling(delay) {
    this.#startDeadline();

    this.pollingRequest = this.#preparePollingRequest(delay);
    this.pollingRequest
      .perform()
      .then(async (response) => {
        const { statusCode } = response;

        switch (statusCode) {
          case 200: {
            this.#clearDeadline();
            const { data } = await response.json;
            if (!resolved.has(this.pollPathValue)) {
              this.dispatch(`${this.resourceIdValue}:succeeded`, { detail: { data } });
              resolved.set(this.pollPathValue, data);
            }
            const imageUrl = this.#variantUrl(data);
            return this.#fetchImage(imageUrl);
          }
          case 202:
            return this.#schedulePolling(this.#requestDelay);
          case 500:
            throw new Error('image generation failed');
          default:
            throw new Error(`unknown HTTP status ${statusCode}, halting execution`);
        }
      })
      .catch((e) => {
        if (e?.name === 'AbortError') {
          return;
        }

        console.error(e);

        this.#displayFailureImage();
        this.element.classList.add(FAILED_CLASS);
        this.dispatch(`${this.resourceIdValue}:failed`);
      });
  }

  #startDeadline() {
    if (!this.deadlineTimeoutId) {
      this.deadlineTimeoutId = this.timeout(ONE_MINUTE, () =>
        this.pollingRequest?.cancel(new Error('image generation polling timed out'))
      );
    }
  }

  #clearDeadline() {
    this.clearTimeout(this.deadlineTimeoutId);
    this.deadlineTimeoutId = null;
  }

  #preparePollingRequest(delay) {
    let request = new FetchRequest('GET', this.pollPathValue, { redirect: 'error' });
    request = new DelayedRequest(request, delay);
    // In some cases, like Draggable making an entire copy of an element, or when the same
    // board rendered multiple times in notifications drawer, it may lead to excessive
    // amount of requests to the same resource. Here's where CoalescedRequest becomes handy.
    request = new CoalescedRequest(request);
    return request;
  }

  #fetchImage(url) {
    const handleLoad = (event) => {
      this.loadedImageSource = url;
      this.imageFailed = event.type === 'error';
      // If the element is currently hidden, the animation doesn't play.
      // So we force it to be shown.
      if (!this.element.checkVisibility()) {
        this.showImage();
      }
    };

    const image = new Image();
    image.addEventListener('load', handleLoad);
    // we aren't going to specifically handle images that failed to load,
    // so we fall back to browser behavior in that case
    image.addEventListener('error', handleLoad);
    image.src = url;
  }

  #displayFailureImage() {
    const { src, alt } = this.placeholderTarget;
    this.placeholderTargetProperties = { src, alt };

    Object.assign(this.placeholderTarget, {
      src: this.failureImagePathValue,
      alt: 'Image failed to generate',
    });
  }

  #variantUrl(data) {
    const { variants } = data;
    return variants[this.variantValue];
  }

  #requestRetry() {
    let request = new FetchRequest('POST', this.retryPathValue);
    request = new CoalescedRequest(request);
    return request.perform();
  }

  /**
   * A random number between BASE_DELAY and BASE_DELAY * 2 - 1
   * @return {Number}
   */
  get #requestDelay() {
    return BASE_DELAY + randomNumber(BASE_DELAY);
  }
}
